import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { creatorOnboardingService } from '../services/creatorOnboardingService';
import { ONBOARDING_STEPS, markStepDone } from '../components/UserOnboarding/onboardingSteps';

/*
 * Returns the current active onboarding step for the signed-in user, or null
 * when onboarding is complete / user is unauthenticated / email unverified.
 *
 * Also exposes a `completeStep(stepId)` action which marks a step done and
 * recomputes the active step. Consumers should call it when the user finishes
 * or skips a step.
 */
export const useOnboarding = () => {
  const { currentUser } = useAuth();
  const [bannerState, setBannerState] = useState(null);
  // The user the banner state was loaded for. Until then the creator step is held back, so a user who already
  // opted out on another device does not see the modal flash on every page load.
  const [bannerLoadedFor, setBannerLoadedFor] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!currentUser) return;
    const userId = currentUser.id;
    creatorOnboardingService
      .getBannerState()
      .then((s) => setBannerState(s))
      .catch(() => setBannerState(null))
      .finally(() => setBannerLoadedFor(userId));
  }, [currentUser, tick]);

  const activeStep = useMemo(() => {
    if (!currentUser || !currentUser.emailVerified) return null;
    const bannerLoaded = bannerLoadedFor === currentUser.id;
    const next = ONBOARDING_STEPS.find((s) => {
      if (s.id === 'become-creator' && !bannerLoaded) return false;
      return !s.isComplete(currentUser, bannerState);
    });
    if (!next) return null;
    const index = ONBOARDING_STEPS.indexOf(next);
    return { ...next, index, total: ONBOARDING_STEPS.length };
  }, [currentUser, bannerState, bannerLoadedFor, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const completeStep = useCallback(
    (stepId) => {
      if (!currentUser?.id) return;
      markStepDone(currentUser.id, stepId);
      if (stepId === 'become-creator') {
        creatorOnboardingService.trackBannerInteraction('opted_out').catch(() => {});
      }
      setTick((n) => n + 1);
    },
    [currentUser?.id]
  );

  const skipStep = useCallback(
    (stepId) => {
      if (!currentUser?.id) return;
      markStepDone(currentUser.id, stepId);
      if (stepId === 'become-creator') {
        creatorOnboardingService.trackBannerInteraction('dismissed').catch(() => {});
      }
      setTick((n) => n + 1);
    },
    [currentUser?.id]
  );

  return { activeStep, completeStep, skipStep };
};

export default useOnboarding;
